import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { adminApi } from '../api/admin';
import { useUsersQuery } from '../api/queries';
import KpiTile from '../components/KpiTile';
import UserCreateForm from '../components/UserCreateForm';
import UserDetailDrawer from '../components/UserDetailDrawer';
import UsersTable from '../components/UsersTable';
import { showToast } from '../components/Toast';
import { useAuth } from '../hooks/useAuth';
import type { UserRow } from '../types';

export default function UsersView() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { data: users, isLoading, error } = useUsersQuery(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [selected, setSelected] = useState<UserRow | null>(null);
  const [syncing, setSyncing] = useState(false);

  async function handleImpersonate(u: UserRow) {
    if (u.id === null) return;
    if (!confirm(`Impersonate ${u.name ?? u.email} (${u.role})?`)) return;
    try {
      await adminApi.impersonate(u.id);
      queryClient.clear();
      window.location.assign('/');
    } catch (err) {
      showToast(`Impersonate failed: ${(err as Error).message}`, { variant: 'error' });
    }
  }

  function refetch() {
    queryClient.invalidateQueries({ queryKey: ['admin-users'] });
  }

  async function handleSyncSheet() {
    setSyncing(true);
    try {
      const res = await adminApi.syncSheet();
      showToast(
        `Sheet sync complete: ${res.synced ?? 0} updated, ${res.created ?? 0} new franchisees, ${res.usersCreated ?? 0} new users`,
        { durationMs: 4500 },
      );
      refetch();
    } catch (err) {
      showToast(`Sync failed: ${(err as Error).message}`, { variant: 'error' });
    } finally {
      setSyncing(false);
    }
  }

  async function handleSendInviteFromRow(u: UserRow) {
    if (!confirm(`Send invite to ${u.email}?`)) return;
    try {
      await adminApi.sendInvite({
        email: u.email,
        name: u.name ?? undefined,
        role: u.role,
        afNumber: u.afNumber ?? undefined,
      });
      showToast(`Invite sent to ${u.email}`);
      refetch();
    } catch (err) {
      showToast((err as Error).message, { variant: 'error' });
    }
  }

  if (isLoading) {
    return (
      <div className="listings-loading">
        <div className="spinner" />
        Loading users...
      </div>
    );
  }
  if (error) {
    return (
      <div style={{ textAlign: 'center', padding: 48, color: '#c0392b' }}>
        Failed to load users: {(error as Error).message}
      </div>
    );
  }

  const list = users ?? [];
  const admins = list.filter((u) => u.role === 'admin');
  const fbcs = list.filter((u) => u.role === 'fbc');
  const franchisees = list.filter((u) => u.role === 'franchisee');
  const active = list.filter((u) => u.pulseStatus === 'active');
  const pending = list.filter((u) => u.pulseStatus === 'pending');

  return (
    <>
      <div className="ds-view-header">
        <div className="ds-view-header-title">
          <h1>User Management</h1>
          <p>
            {list.length} accounts · {active.length} active · {pending.length} pending invitation
          </p>
        </div>
        <div className="ds-view-header-actions">
          <button
            type="button"
            className="ds-btn ds-btn-secondary"
            onClick={handleSyncSheet}
            disabled={syncing}
          >
            {syncing ? 'Syncing…' : 'Sync Google Sheet'}
          </button>
          <button
            type="button"
            className="ds-btn ds-btn-primary"
            onClick={() => setCreateOpen((v) => !v)}
          >
            + New User
          </button>
        </div>
      </div>

      <div className="ds-kpi-grid">
        <KpiTile label="Total Users" value={list.length} sub="All roles" />
        <KpiTile
          label="Active"
          value={<span style={{ color: '#16a34a' }}>{active.length}</span>}
          sub="Have signed in"
        />
        <KpiTile
          label="Pending"
          value={<span style={{ color: '#f59e0b' }}>{pending.length}</span>}
          sub="Invite sent"
        />
        <KpiTile label="Admins" value={admins.length} sub="Full access" />
        <KpiTile label="Coaches" value={fbcs.length} sub="FBC accounts" />
        <KpiTile label="Franchisees" value={franchisees.length} sub="Location owners" />
      </div>

      <UserCreateForm
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={() => {
          setCreateOpen(false);
          refetch();
        }}
      />

      <UsersTable
        rows={list}
        onSelect={setSelected}
        onSendInvite={handleSendInviteFromRow}
        onImpersonate={handleImpersonate}
        currentUserId={user?.id ?? null}
      />

      {selected && (
        <UserDetailDrawer
          user={selected}
          onClose={() => setSelected(null)}
          onMutated={() => {
            refetch();
          }}
        />
      )}
    </>
  );
}
